import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Icon } from 'semantic-ui-react';
import { useNoteContext } from '../context/NoteContext';
import { formatCurrency, formatPercentage } from '../utilities/calculations';

interface DiscountAnalysisModalProps {
  open: boolean;
  onClose: () => void;
}

export const DiscountAnalysisModal: React.FC<DiscountAnalysisModalProps> = ({ open, onClose }) => {
  const { inputs, calculations, updateInput, hasRequiredInputs } = useNoteContext();
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const prevOpenRef = useRef(open);

  // Initialize discount percentage when modal opens based on existing desired price
  useEffect(() => {
    if (open && !prevOpenRef.current && inputs.listedPurchasePrice > 0) {
      // Modal just opened - initialize slider position
      if (inputs.desiredPurchasePrice > 0 && inputs.desiredPurchasePrice < inputs.listedPurchasePrice) {
        // Calculate discount percentage from existing desired price
        const calculatedDiscount = ((inputs.listedPurchasePrice - inputs.desiredPurchasePrice) / inputs.listedPurchasePrice) * 100;
        setDiscountPercentage(calculatedDiscount);
      } else {
        // Reset to 0 if no valid desired price exists
        setDiscountPercentage(0);
      }
    }
    prevOpenRef.current = open;
  }, [open, inputs.listedPurchasePrice, inputs.desiredPurchasePrice]);

  // Update desired purchase price when discount percentage changes (only when modal is open)
  useEffect(() => {
    if (open && inputs.listedPurchasePrice > 0) {
      const desiredPrice = inputs.listedPurchasePrice * (1 - discountPercentage / 100);
      updateInput('desiredPurchasePrice', desiredPrice);
    }
  }, [open, discountPercentage, inputs.listedPurchasePrice, updateInput]);

  const handleDiscountChange = (value: number) => {
    setDiscountPercentage(value);
  };

  if (!hasRequiredInputs) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="percent" />
        Discount Analysis - Interactive
      </Modal.Header>
      <Modal.Content>
        <div className="mortgage-analyzer">
          <div className="discount-analysis-section">
            {/* Discount Percentage Slider */}
            <div className="slider-control">
              <div className="slider-header">
                <span className="slider-label">
                  <Icon name="sliders horizontal" /> Adjust Discount Percentage
                </span>
                <span className="slider-value">
                  {discountPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="slider-input">
                <input
                  type="range"
                  min="0"
                  max="50"
                  step="0.5"
                  value={discountPercentage}
                  onChange={(e) => handleDiscountChange(parseFloat(e.target.value))}
                />
              </div>
              <div className="slider-labels">
                <span>0% (No Discount)</span>
                <span>25%</span>
                <span>50% (Maximum)</span>
              </div>

              {discountPercentage > 0 && (
                <div className="calculated-price">
                  <div className="price-label">Your Discounted Purchase Price</div>
                  <div className="price-value">
                    {formatCurrency(inputs.desiredPurchasePrice)}
                  </div>
                  <div className="price-label" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                    (Savings: {formatCurrency(inputs.listedPurchasePrice - inputs.desiredPurchasePrice)})
                  </div>
                </div>
              )}
            </div>

            {/* Live Metrics - Only show when discount is applied */}
            {discountPercentage > 0 && (
            <>
              <div className="divider-section">
                <span className="divider-text">Live Discount Metrics</span>
              </div>

              <div className="live-metrics">
                <div className="live-metric-card">
                  <div className="metric-label">Discounted Investment</div>
                  <div className="metric-value">
                    {formatCurrency(calculations.desiredPurchasePriceWithFees)}
                  </div>
                </div>

                <div className="live-metric-card">
                  <div className="metric-label">Total Profit</div>
                  <div className="metric-value positive">
                    {formatCurrency(calculations.actualProfit)}
                  </div>
                </div>

                <div className="live-metric-card">
                  <div className="metric-label">Annual Profit</div>
                  <div className="metric-value positive">
                    {formatCurrency(calculations.annualProfit)}
                  </div>
                </div>

                <div className="live-metric-card">
                  <div className="metric-label">Discounted Annualized ROI</div>
                  <div className="metric-value highlight">
                    {formatPercentage(calculations.discountedAnnualizedROI)}
                  </div>
                </div>

                <div className="live-metric-card">
                  <div className="metric-label">Discounted Total ROI</div>
                  <div className="metric-value highlight">
                    {formatPercentage(calculations.discountedROI)}
                  </div>
                </div>

                <div className="live-metric-card">
                  <div className="metric-label">Discounted IRR</div>
                  <div className="metric-value highlight">
                    {formatPercentage(calculations.discountedIRR)}
                  </div>
                </div>
              </div>

              {/* Full Comparison Section */}
              <div className="divider-section" style={{ marginTop: '1.5rem' }}>
                <span className="divider-text">Full Discount Comparison</span>
              </div>

              <div className="analysis-grid" style={{ marginTop: '0.5rem' }}>
                <div className="analysis-card">
                  <div className="analysis-header">Original Price</div>
                  <div className="analysis-value">
                    {formatCurrency(inputs.listedPurchasePrice)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Discounted Price</div>
                  <div className="analysis-value highlight-purple">
                    {formatCurrency(inputs.desiredPurchasePrice)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">You Save</div>
                  <div className="analysis-value highlight-green">
                    {formatCurrency(inputs.listedPurchasePrice - inputs.desiredPurchasePrice)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Desired Price (with fees)</div>
                  <div className="analysis-value highlight-purple">
                    {formatCurrency(calculations.desiredPurchasePriceWithFees)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Discount Percentage</div>
                  <div className="analysis-value highlight-purple">
                    {formatPercentage(calculations.discount)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Actual Profit</div>
                  <div className="analysis-value highlight-green">
                    {formatCurrency(calculations.actualProfit)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Annual Profit</div>
                  <div className="analysis-value highlight-green">
                    {formatCurrency(calculations.annualProfit)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Discounted Annualized ROI</div>
                  <div className="analysis-value highlight-orange">
                    {formatPercentage(calculations.discountedAnnualizedROI)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Discounted Total ROI</div>
                  <div className="analysis-value highlight-orange">
                    {formatPercentage(calculations.discountedROI)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">Discounted IRR</div>
                  <div className="analysis-value highlight-orange">
                    {formatPercentage(calculations.discountedIRR)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">ROI Improvement</div>
                  <div className="analysis-value highlight-green">
                    {formatPercentage(calculations.discountedROI - calculations.roi)}
                  </div>
                </div>

                <div className="analysis-card">
                  <div className="analysis-header">IRR Improvement</div>
                  <div className="analysis-value highlight-green">
                    {formatPercentage(calculations.discountedIRR - calculations.irr)}
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="check" /> Done
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
